import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalizedMethods } from "@/hooks/useLocalizedMethods";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { BottomNav, Tab } from "@/components/BottomNav";
import { 
  Sparkles, User as UserIcon, PanelLeftClose, PanelLeft,
  History, Heart, Telescope, Scale, Target, Zap, Shield, 
  Users, Globe, RefreshCw, ArrowRight, Circle 
} from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AppLayout } from "@/components/AppLayout";

const LoopExplanation = () => {
  const { t } = useTranslation();
  const { localizeMethods } = useLocalizedMethods();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<Tab>("loop");
  const [rawMethods, setRawMethods] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);

  const methods = localizeMethods(rawMethods);

  useEffect(() => {
    const loadData = async () => {
      const [methodsResult, userResult] = await Promise.all([
        supabase.from("training_methods").select("*").order("category"),
        supabase.auth.getUser()
      ]);
      
      if (methodsResult.data) setRawMethods(methodsResult.data);
      
      if (userResult.data?.user) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", userResult.data.user.id)
          .maybeSingle();
        if (profileData) setProfile(profileData);
      }
    };
    
    loadData();
  }, []);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    if (tab === "profile") {
      navigate("/profile");
    } else if (tab === "chat") {
      navigate("/chat");
    }
  };

  const handleMethodClick = (method: any) => {
    navigate("/chat", { state: { selectedMethod: method } });
  };

  const categories = Array.from(new Set(methods.map(m => m.category)));
  const methodCounts = methods.reduce((acc, method) => {
    acc[method.category] = (acc[method.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const phaseIcons = {
    1: Circle,
    2: Target,
    3: Zap,
    4: RefreshCw
  };

  const elementIcons: Record<string, any> = {
    "PIH": History,
    "CiV": Heart,
    "PiF2": Telescope,
    "RaN": Scale,
    "PiF1s": Target,
    "PiF1t": Zap,
    "PiF1o": Shield,
    "RoF": Users,
    "ERoF": Globe,
    "SYNC": RefreshCw
  };

  // Toggle button component
  const ToggleButton = () => {
    const { toggleSidebar, state } = useSidebar();
    const isCollapsed = state === "collapsed";

    return (
      <button
        onClick={toggleSidebar}
        className="p-2 hover:bg-sidebar-accent/20 rounded-md transition-colors"
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? (
          <PanelLeft className="h-5 w-5" />
        ) : (
          <PanelLeftClose className="h-5 w-5" />
        )}
      </button>
    );
  };

  // Mobile view
  if (isMobile) {
    return (
      <>
        <div className="min-h-screen flex flex-col pb-16">
          <header className="h-16 border-b bg-card flex items-center px-4 gap-3 sticky top-0 z-40">
            <Sparkles className="h-6 w-6 text-primary flex-shrink-0" />
            <h1 className="text-lg font-bold truncate">Juni</h1>
            <div className="ml-auto">
              <Avatar className="w-8 h-8" onClick={() => setActiveTab("profile")}>
                <AvatarImage src={profile?.avatar_url} />
                <AvatarFallback>
                  {profile?.name?.charAt(0).toUpperCase() || <UserIcon className="w-4 h-4" />}
                </AvatarFallback>
              </Avatar>
            </div>
          </header>

          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="max-w-5xl mx-auto p-6 space-y-8 pb-20">
                {/* Content */}
                <LoopContent t={t} />
              </div>
            </ScrollArea>
          </div>
        </div>

        <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Desktop view
  return (
    <AppLayout activeView="loop" profile={profile}>
      <ScrollArea className="flex-1">
            <div className="max-w-5xl mx-auto p-6 space-y-8 pb-20">
              {/* Content */}
              <LoopContent t={t} />
            </div>
          </ScrollArea>
    </AppLayout>
  );
};

// Separate content component to avoid duplication
const LoopContent = ({ t }: { t: any }) => {
  return (
    <>
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-foreground">
          {t('loop.juniTitle', 'JUNI und der LOOP')}
        </h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          {t('loop.subtitle')}
        </p>
      </div>

      {/* Juni & der LOOP */}
      <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-3">
            <Sparkles className="h-7 w-7 text-primary" />
            {t('loop.juniSupport.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-lg leading-relaxed">
            {t('loop.juniSupport.intro')}
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-primary" />
                  {t('loop.juniSupport.library.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                {t('loop.juniSupport.library.description')}
              </CardContent>
            </Card>

            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  {t('loop.juniSupport.situational.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                {t('loop.juniSupport.situational.description')}
              </CardContent>
            </Card>

            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  {t('loop.juniSupport.support247.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                {t('loop.juniSupport.support247.description')}
              </CardContent>
            </Card>

            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  {t('loop.juniSupport.continuous.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                {t('loop.juniSupport.continuous.description')}
              </CardContent>
            </Card>
          </div>

          <div className="bg-primary/10 p-4 rounded-lg text-center border border-primary/20">
            <p className="text-base font-medium">
              <strong>{t('loop.juniSupport.framework')}</strong> · <strong>{t('loop.juniSupport.guide')}</strong>
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t('loop.juniSupport.practical')}
            </p>
          </div>

          <div className="flex justify-center">
            <Button size="lg" onClick={() => window.location.href = '/chat'} className="gap-2">
              <Sparkles className="h-5 w-5" />
              {t('loop.juniSupport.cta')}
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Der JCI Loop Überschrift */}
      <div className="text-center space-y-4 my-12">
        <h2 className="text-3xl md:text-4xl font-bold text-foreground">
          {t('loop.title')}
        </h2>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          {t('loop.subtitle')}
        </p>
      </div>

      {/* Die Herausforderung */}
      <Card className="border-destructive/20">
        <CardHeader>
          <CardTitle className="text-2xl">{t('loop.challenge.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <h3 className="text-xl font-semibold">{t('loop.challenge.subtitle')}</h3>
          <p className="text-muted-foreground">
            {t('loop.challenge.intro')}
          </p>
          
          <div className="grid gap-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('loop.challenge.vision.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="font-medium">{t('loop.challenge.vision.challenge')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('loop.challenge.vision.description')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('loop.challenge.strategy.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="font-medium">{t('loop.challenge.strategy.challenge')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('loop.challenge.strategy.description')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('loop.challenge.tactics.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="font-medium">{t('loop.challenge.tactics.challenge')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('loop.challenge.tactics.description')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('loop.challenge.feedback.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="font-medium">{t('loop.challenge.feedback.challenge')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('loop.challenge.feedback.description')}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-muted p-4 rounded-lg mt-6 text-center">
            <p className="text-lg font-medium italic">
              "{t('loop.challenge.question')}"
            </p>
          </div>

          <p className="text-center text-muted-foreground mt-4">
            {t('loop.challenge.conclusion')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t('loop.foundations.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            {t('loop.foundations.intro')}
          </p>

          <div className="grid gap-4 mt-6">
            <Card className="border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  {t('loop.foundations.values.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="font-medium">{t('loop.foundations.values.function')}</p>
                <p className="text-sm text-muted-foreground">
                  <strong>{t('loop.challenge.vision.challenge').split(':')[0]}:</strong> {t('loop.foundations.values.challenge').split(':')[1]}
                </p>
              </CardContent>
            </Card>

            <Card className="border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="h-5 w-5 text-primary" />
                  {t('loop.foundations.rules.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="font-medium">{t('loop.foundations.rules.function')}</p>
                <p className="text-sm text-muted-foreground">
                  <strong>{t('loop.challenge.vision.challenge').split(':')[0]}:</strong> {t('loop.foundations.rules.challenge').split(':')[1]}
                </p>
              </CardContent>
            </Card>

            <Card className="border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5 text-primary" />
                  {t('loop.foundations.history.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="font-medium">{t('loop.foundations.history.function')}</p>
                <p className="text-sm text-muted-foreground">
                  <strong>{t('loop.challenge.vision.challenge').split(':')[0]}:</strong> {t('loop.foundations.history.challenge').split(':')[1]}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-muted p-4 rounded-lg mt-6 text-center">
            <p className="text-lg font-medium italic">
              "{t('loop.foundations.question')}"
            </p>
          </div>

          <p className="text-center text-muted-foreground mt-4">
            {t('loop.foundations.conclusion')}
          </p>
        </CardContent>
      </Card>

      {/* Die Logik */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-center">Die Logik</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-center text-muted-foreground">
            9 Elemente. Hunderte von dokumentierten Abhängigkeiten. Ein System.
          </p>

          {/* Die zeitbasierte Kaskade */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Circle className="h-5 w-5 text-primary" />
              Die zeitbasierte Kaskade
            </h3>
            
            {/* 2x2 Grid mit Pfeilen */}
            <div className="space-y-4">
              {/* Erste Reihe: Vision -> Strategie */}
              <div className="grid grid-cols-[1fr,auto,1fr] gap-4 items-center">
                <Card className="border-purple-200 bg-purple-50 dark:bg-purple-950/20">
                  <CardHeader className="pb-2">
                    <div className="text-sm font-medium text-purple-600 dark:text-purple-400">PiF2</div>
                    <CardTitle className="text-lg">Vision</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">10+ Jahre oder zeitlos</p>
                  </CardContent>
                </Card>

                <ArrowRight className="h-6 w-6 text-muted-foreground" />

                <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
                  <CardHeader className="pb-2">
                    <div className="text-sm font-medium text-blue-600 dark:text-blue-400">PiF1s</div>
                    <CardTitle className="text-lg">Strategie</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">5-10 Jahre</p>
                  </CardContent>
                </Card>
              </div>

              {/* Zweite Reihe: Taktik -> Daily Business */}
              <div className="grid grid-cols-[1fr,auto,1fr] gap-4 items-center">
                <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
                  <CardHeader className="pb-2">
                    <div className="text-sm font-medium text-green-600 dark:text-green-400">PiF1t</div>
                    <CardTitle className="text-lg">Taktik</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">1-5 Jahre</p>
                  </CardContent>
                </Card>

                <ArrowRight className="h-6 w-6 text-muted-foreground" />

                <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
                  <CardHeader className="pb-2">
                    <div className="text-sm font-medium text-orange-600 dark:text-orange-400">PiF1o</div>
                    <CardTitle className="text-lg">Daily Business</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">Sprints, Wochen</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Die Fundamente und Enabler als 2 Spalten */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {/* Die Fundamente */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <History className="h-5 w-5 text-primary" />
                Die Fundamente
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Heart className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">CiV - Core influential Values</p>
                    <p className="text-sm text-muted-foreground">Leiten alle Entscheidungen</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Scale className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">RaN - Rules and Norms</p>
                    <p className="text-sm text-muted-foreground">Operationalisieren Werte</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <History className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">PIH - Point in History</p>
                    <p className="text-sm text-muted-foreground">Organisationsgedächtnis</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Die Enabler und ihre Umwelt */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Die Enabler und ihre Umwelt
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">RoF - Role of Function</p>
                    <p className="text-sm text-muted-foreground">Wer macht was?</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">ERoF - Environment RoF</p>
                    <p className="text-sm text-muted-foreground">Kontext & Dynamiken</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

          {/* The Logic of the LOOP */}
          <Card>
            <CardHeader>
              <CardTitle>{t('loop.logic.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">{t('loop.logic.vertical.title')}</h4>
                  <p className="text-muted-foreground text-sm">{t('loop.logic.vertical.description')}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">{t('loop.logic.horizontal.title')}</h4>
                  <p className="text-muted-foreground text-sm">{t('loop.logic.horizontal.description')}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">{t('loop.logic.cycle.title')}</h4>
                  <p className="text-muted-foreground text-sm">{t('loop.logic.cycle.description')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Application */}
          <Card>
            <CardHeader>
              <CardTitle>{t('loop.application.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">{t('loop.application.intro')}</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-muted-foreground">{t('loop.application.personal')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-muted-foreground">{t('loop.application.team')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-muted-foreground">{t('loop.application.organization')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-muted-foreground">{t('loop.application.project')}</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4 italic">
                {t('loop.application.conclusion')}
              </p>
            </CardContent>
          </Card>
        </>
      );
    };

export default LoopExplanation;
